'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Maximize2 } from 'lucide-react';
import type { FredRange } from '@/lib/fred-series';
import { TimeSeriesChart, prepareSeries, type RecessionRange } from './TimeSeriesChart';
import { ChartExpandModal } from './ChartExpandModal';

const TITLE = 'Shiller PE Ratio (CAPE)';
const DESCRIPTION =
  'Cyclically adjusted P/E for the S&P 500 using 10-year average real earnings. Long-run valuation benchmark.';
const COLOR = '#EC4899';

interface ShillerPePayload {
  seriesId: string;
  range: FredRange;
  observations: { date: string; value: number }[];
  lastUpdated: string;
}

interface ShillerPeChartProps {
  range: FredRange;
  recessionRanges: RecessionRange[];
  height?: number;
}

export function ShillerPeChart({ range, recessionRanges, height = 220 }: ShillerPeChartProps) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<ShillerPePayload>({
    queryKey: ['/api/terminal/shiller-pe', range],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/shiller-pe?range=${range}`);
      if (!res.ok) throw new Error('Failed to fetch Shiller PE');
      return res.json();
    },
    staleTime: 12 * 60 * 60 * 1000,
    retry: 1,
  });

  const prepared = useMemo(() => prepareSeries(data?.observations), [data?.observations]);
  const { hasData } = prepared;

  return (
    <div className="bg-[#111827] border border-[#1F2937] p-4">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">
          {TITLE}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={`Expand ${TITLE}`}
          title="Expand for detail"
          className="shrink-0 -mt-0.5 -mr-1 p-1 text-[#6B7280] hover:text-[var(--color-accent)] hover:bg-[#1F2937] rounded transition-colors"
        >
          <Maximize2 size={13} />
        </button>
      </div>
      <div className="text-[11px] text-[#6B7280] mb-3">{DESCRIPTION}</div>

      {isLoading ? (
        <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height }} />
      ) : isError || !hasData ? (
        <div
          className="w-full flex items-center justify-center text-xs text-[#6B7280] border border-dashed border-[#1F2937]"
          style={{ height }}
        >
          <div className="text-center">
            <div>{isError ? 'Data unavailable' : 'No data'}</div>
            {isError && (
              <button
                onClick={() => refetch()}
                className="mt-2 px-2 py-0.5 text-[10px] text-[var(--color-accent)] hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      ) : (
        <TimeSeriesChart
          prepared={prepared}
          recessionRanges={recessionRanges}
          color={COLOR}
          unit=""
          chartType="line"
          seriesLabel="Shiller PE"
          height={height}
        />
      )}

      <ChartExpandModal
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        title={TITLE}
        description={DESCRIPTION}
        color={COLOR}
        unit=""
        chartType="line"
        seriesLabel="Shiller PE"
        initialRange={range}
        recessionRanges={recessionRanges}
        queryKey={(r) => ['/api/terminal/shiller-pe', r]}
        fetchUrl={(r) => `/api/terminal/shiller-pe?range=${r}`}
      />
    </div>
  );
}
